import { cn } from '@/lib/utils';
import { AutoChip } from './ClockStrip';

/**
 * Prompt 1 / 6: the horizontal pick ticker.
 *
 * ── SAB-845 BAIL-OUT, PRESERVED ─────────────────────────────────────────────
 *
 * The shipped DraftOrderStrip refuses to render at all while an order-rewriting sabotage
 * (reverse_order / skip_pick) is live, and that behaviour is deliberate and load-bearing.
 * nfl_draft_picks holds BASE SNAKE ORDER and only the current row is rewritten at pick time,
 * so every upcoming card in this ticker would name the wrong team. A ticker that confidently
 * says "2.04 · YOU" when the sabotage moved your pick is worse than no ticker: the user
 * plans around it.
 *
 * This ticker therefore takes `orderScrambled` and renders the same bail-out. It does NOT
 * render "best effort with a warning" -- a warning next to a specific wrong team name is
 * still a specific wrong team name.
 */

export interface TickerEntry {
  pickNumber: number;
  round: number;
  pickInRound: number;
  teamId: string;
  teamName: string;
}

export interface PickTickerProps {
  entries: TickerEntry[];
  currentPick: number;
  myTeamId?: string | null;
  /**
   * Teams on autodraft NOW, from nfl_teams.auto_draft_enabled (SAB-992). Was previously
   * derived from pick history, which could not track a mid-draft toggle.
   */
  autopickTeamIds?: Set<string>;
  /** SAB-845: true while reverse_order or skip_pick is active. */
  orderScrambled?: boolean;
}

export default function PickTicker({
  entries, currentPick, myTeamId, autopickTeamIds, orderScrambled,
}: PickTickerProps) {
  if (orderScrambled) {
    return (
      <div
        data-testid="ticker-scrambled-bailout"
        className="flex items-center gap-2 rounded-[9px] border border-damage/40 bg-damage/10 px-3 py-2"
      >
        <span className="font-display font-bold text-[10px] tracking-[0.08em] text-damage">
          ORDER SCRAMBLED
        </span>
        <span className="text-[11px] text-muted-foreground truncate">
          Upcoming picks are hidden while a sabotage is rewriting the order.
        </span>
      </div>
    );
  }

  return (
    <div data-testid="pick-ticker" className="flex gap-2 overflow-x-auto overflow-y-hidden">
      {entries.map((e, i) => {
        const isCurrent = e.pickNumber === currentPick;
        const isMine = !!myTeamId && e.teamId === myTeamId;
        const isAuto = autopickTeamIds?.has(e.teamId) ?? false;
        const label = `${e.round}.${String(e.pickInRound).padStart(2, '0')}`;

        return (
          <div
            key={e.pickNumber}
            data-testid="ticker-card"
            data-current={isCurrent || undefined}
            data-mine={isMine || undefined}
            data-auto={isAuto || undefined}
            className={cn(
              'flex-none w-[118px] rounded-[9px] border px-2.5 py-2',
              isCurrent
                ? 'border-primary bg-primary/[0.14]'
                : isMine
                  ? 'border-success/40 bg-success/[0.08]'
                  : isAuto
                    ? 'border-[hsl(220_90%_56%/0.35)] bg-card'
                    : 'border-border bg-card',
              // Cards beyond the visible run fade, per the design.
              i > 6 && 'opacity-55',
            )}
          >
            <div className="flex items-center justify-between gap-1">
              <span
                className={cn(
                  'font-display font-bold text-[9px] tracking-[0.08em]',
                  isCurrent ? 'text-primary' : isMine ? 'text-success' : 'text-muted-foreground',
                )}
              >
                {isCurrent ? `NOW · ${label}` : isMine ? `${label} · YOU` : label}
              </span>
              {isAuto && <AutoChip />}
            </div>
            <div className="mt-0.5 truncate text-[13px] font-bold text-white">{e.teamName}</div>
          </div>
        );
      })}
    </div>
  );
}
